import { useCallback, useEffect, useState } from 'react';
import { Ban } from 'lucide-react';
import { toast } from 'sonner';
import { getPizasWaBlacklist, updatePizasWaBlacklist } from '../lib/api';
import { entriesFromApi, formatPhone10, phone10 } from '../lib/waBlacklistUtils';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

export default function WaBlacklistCard() {
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blName, setBlName] = useState('');
  const [blPhone, setBlPhone] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const bl = await getPizasWaBlacklist();
      setBlacklist(entriesFromApi(bl));
    } catch {
      toast.error('No se pudo cargar la blacklist.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persist = async (next) => {
    const saved = await updatePizasWaBlacklist(next);
    setBlacklist(entriesFromApi(saved));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const p10 = phone10(blPhone);
    if (!p10) {
      toast.error('Ingresa un teléfono válido (10 dígitos, México).');
      return;
    }
    if (blacklist.some((x) => x.phone_digits === p10)) {
      toast.error('Ese número ya está en la blacklist.');
      return;
    }
    setAdding(true);
    try {
      await persist([...blacklist, { phone_digits: p10, name: blName.trim() || 'Bloqueo manual' }]);
      setBlName('');
      setBlPhone('');
      toast.success('Número agregado a la blacklist.');
    } catch {
      toast.error('No se pudo agregar el número.');
    } finally {
      setAdding(false);
    }
  };

  const handleUnblock = async (phone) => {
    try {
      await persist(blacklist.filter((x) => x.phone_digits !== phone));
      toast.success('Número desbloqueado.');
    } catch {
      toast.error('No se pudo desbloquear.');
    }
  };

  return (
    <Card className="max-w-xl border border-border" data-testid="wa-blacklist-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Ban className="w-5 h-5 text-primary" />
          Blacklist WhatsApp
        </CardTitle>
        <CardDescription>
          Solo administradores. Los números bloqueados no pueden iniciar pedidos por WhatsApp. Se agregan
          automáticamente al marcar pickup como no recogido, o manualmente aquí.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <p className="text-sm text-muted-foreground animate-pulse">Cargando blacklist…</p>
        ) : (
          <>
            <form onSubmit={handleAdd} className="grid gap-4 sm:grid-cols-2 border-b border-border pb-6">
              <div className="space-y-2">
                <Label htmlFor="bl_name">Nombre</Label>
                <Input
                  id="bl_name"
                  value={blName}
                  onChange={(e) => setBlName(e.target.value)}
                  placeholder="Ej. Juan Pérez"
                  maxLength={120}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bl_phone">Teléfono</Label>
                <Input
                  id="bl_phone"
                  type="tel"
                  value={blPhone}
                  onChange={(e) => setBlPhone(e.target.value)}
                  placeholder="10 dígitos o +52…"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" variant="secondary" disabled={adding}>
                  {adding ? 'Agregando…' : 'Agregar a blacklist'}
                </Button>
              </div>
            </form>

            {blacklist.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay números bloqueados.</p>
            ) : (
              <ul className="divide-y divide-border">
                {blacklist.map((row) => (
                  <li key={row.phone_digits} className="flex flex-wrap items-center justify-between gap-3 py-3">
                    <div>
                      <p className="font-medium text-foreground">{row.name || 'Sin nombre'}</p>
                      <p className="font-mono text-sm text-muted-foreground">
                        {formatPhone10(row.phone_digits)}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleUnblock(row.phone_digits)}>
                      Desbloquear
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
